<template>
  <div id='purchases-screen'>
    <div class="container hungarospa-container">
      <header>
        <h1 class="title">{{ $t('pages.purchases.title') }}</h1>
        <span class="subtitle">{{ $t('pages.purchases.subtitle') }}</span>
      </header>
      <div class="checkbox-wrapper">
        <base-checkbox
            :label="$t('pages.purchases.filter.label')"
            :isChecked="forToday"
            @changed="handleCheckboxInput(...arguments)"
          />
      </div>
      <div class="purchases-section">
        <font-awesome-icon v-if="purchases === undefined" class="loader" :icon="['fa', 'spinner']" />
        <template v-if="purchases && purchases.length === 0">
          <span class="empty-text">{{ $t('pages.purchases.emptyState') }}</span>
        </template>
        <template v-if="purchases !== undefined">
          <div class="table-header">
            <span class="col-name" v-for="(header, i) in tableHeaders" :key="i">{{ header }}</span>
          </div>
          <purchase-row
            v-for="(item, i) in purchases" :key="i"
            :date="item.date"
            :orderRef="item.orderIdentifier"
            :invoiceNumber="getInvoiceNumber(item.invoice)"
            :price="item.price"
            :ticketLink="item.ticketDownloadLink"
            :invoiceLink="item.invoiceDownloadLink"
            :refundTransaction="item.refundTransaction"
            :validityDates="item.validityDates"
            :needDateButton="needDateButton(item)"
            :needRefoundButton="needRefundButton(item)"
            :orderId="item._id"
          />
        </template>
      </div>
      <div class="pagination-bottom">
        <transition
          mode="in-out"
          enter-active-class="animate__animated animate__fadeIn animate__faster"
          leave-active-class="animate__animated animate__fadeOut animate__faster"
        >
          <pagination-base
            v-if="pagesNumber !== undefined && pagesNumber !== 0"
            :pagesNumber="pagesNumber"
            :activePageNumber="currentPageNumber"
            @navigateTriggered="handlePagination"
          />
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

import BaseCheckbox from '@/components/shared/elements/inputs/BaseCheckbox';
import PurchaseRow from '@/components/shared/modules/users/PurchaseRow';
import PaginationBase from '@/components/shared/modules/pagination/PaginationBase';

export default {
  name: 'PurchasesScreen',
  props: {},
  components: {
    PurchaseRow,
    PaginationBase,
    BaseCheckbox,
  },
  data: () => ({
    pagesNumber: undefined,
    currentPageNumber: 0,
    purchases: undefined,
    forToday: false,
  }),
  created() {
    this.getPrevPurchases({ page: this.currentPageNumber, forToday: this.forToday }).then((resp) => {
      this.pagesNumber = Math.ceil(resp.total / resp.limit);
      this.purchases = resp.result;
    });
  },
  computed: {
    tableHeaders() {
      return [
        this.$t('pages.purchases.table.headers.date'),
        this.$t('pages.purchases.table.headers.transactionId'),
        this.$t('pages.purchases.table.headers.invoice'),
        this.$t('pages.purchases.table.headers.price'),
        this.$t('pages.purchases.table.headers.validityDate'),
      ];
    },
  },
  watch: {
    forToday(newVal, oldVal) {
      if (oldVal !== newVal) {
        this.purchases = undefined;
        this.currentPageNumber = 0;
        this.getPrevPurchases({ page: this.currentPageNumber, forToday: newVal }).then((resp) => {
          this.pagesNumber = Math.ceil(resp.total / resp.limit);
          this.purchases = resp.result;
        });
      }
    },
    currentPageNumber(newVal, oldVal) {
      if (oldVal !== newVal) {
        this.purchases = undefined;
        this.getPrevPurchases({ page: newVal, forToday: this.forToday }).then((resp) => {
          this.pagesNumber = Math.ceil(resp.total / resp.limit);
          this.purchases = resp.result;
        });
      }
    },
  },
  methods: {
    ...mapActions({
      getPrevPurchases: 'users/getPrevPurchases',
    }),
    needRefundButton(item) {
      return item.refundTransaction && item.refundTransaction.length === 0 && this.$PAYMENT_PROVIDERS.PROVIDERS_WITH_REFUND.includes(item.provider);
    },
    needDateButton(item) {
      return !item.containsRestrictedItems;
    },
    handleCheckboxInput(value) {
      this.forToday = value;
    },
    handlePagination(pageNumber) {
      this.currentPageNumber = pageNumber;
    },
    getInvoiceNumber(invoice) {
      let localIdentifier;

      if (invoice) {
        // eslint-disable-next-line prefer-destructuring
        localIdentifier = invoice.split('|')[2];
      }
      return localIdentifier;
    },
  },
};
</script>

<style lang='scss' scoped>
$contentMaxWidth: 1200px;

@keyframes loaderAnimation {
  0% {
    transform: rotate(0);
  }
  100% {
    transform: rotate(360deg);
  }
}

#purchases-screen {
  padding: 50px 0 350px;

  @media screen and (max-width: $breakpointDownMd) {
    padding-bottom: 250px;
  }

  @media screen and (max-width: $breakpointDownXs) {
    padding-bottom: 150px;
  }
}

header {
  margin: 0 0 50px;

  .title {
    @include titleMixin();
    font-size: 40px;
    line-height: 1;

    @media screen and (max-width: $breakpointDownMd) {
      font-size: 32px;
    }

    @media screen and (max-width: $breakpointDownXs) {
      font-size: 24px;
      margin-bottom: 10px;
    }
  }

  .subtitle {
    font-weight: 500;
    display: block;
    text-align: center;

    @media screen and (max-width: $breakpointDownXs) {
      font-size: 14px;
      line-height: 1.2;
    }
  }
}

.loader {
  display: inline-block;
  animation: loaderAnimation 1.6s infinite;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 18px;
}

.empty-text {
  font-weight: 500;
  text-align: center;
  display: block;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 18px;
}

.purchases-section {
  border-radius: $globalBorderRadius;
  border: 1px solid $globalBorderColor;
  background-color: $white;
  padding: 0 0 30px;
  max-width: $contentMaxWidth;
  margin: 0 auto;
  position: relative;
  min-height: 200px;
  transition: $transitionBase;
}

.table-header {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  max-width: $contentMaxWidth;
  padding: 20px 30px;
  border-bottom: 1px solid $globalBorderColor;
  margin: 0 0 25px;

  @media screen and (max-width: $breakpointDownMd) {
    grid-template-columns: 0.7fr 0.7fr 0.8fr 0.8fr 1fr;
  }

  @media screen and (max-width: $breakpointDownSm) {
    padding: 15px 18px 15px 10px;
    grid-template-columns: 0.7fr 1.5fr 0.8fr 1fr;
    align-items: center;
  }

  @media screen and (max-width: $breakpointDownXs) {
    grid-template-columns: 1fr 1fr 1fr;
  }

  .col-name {
    font-weight: 500;

    @media screen and (max-width: $breakpointDownMd) {
      white-space: nowrap;
      display: inline-block;
      overflow: hidden;
      text-overflow: ellipsis;
      &:nth-child(2) {
        padding-left: 10px !important;
      }
      &:nth-child(3) {
        padding-left: 60px !important;
      }
      &:nth-child(4) {
        padding-left: 66px;
      }
      &:nth-child(5) {
        padding-left: 34px;
        text-align: right;
      }
    }

    @media screen and (max-width: $breakpointDownSm) {
      &:nth-child(2) {
        max-width: 120px;
        grid-area: 2 / 1 / 3 / 3;
        padding-left: 0 !important;
      }

      &:nth-child(3) {
        grid-area: 1 / 2 / 3 / 2;
        padding-left: 76px !important;
      }

      &:nth-child(4) {
        grid-area: 1 / 3 / 3 / 4;
        padding-left: 0px;
        text-align: center;
      }
      &:nth-child(5) {
        grid-area: 1 / 4 / 4 / 4;
        padding-left: 10px;
        text-align: right;
      }
    }

    @media screen and (max-width: $breakpointDownXs) {
      &:nth-child(1) {
        max-width: 100px;
      }
      &:nth-child(2) {
        max-width: 100px;
      }
      &:nth-child(3) {
        padding-left: 30px !important;
      }
      &:nth-child(4) {
        grid-area: 1 / 2 / 3 / 4;
        padding-left: 108px;
      }

      &:nth-child(5) {
        grid-area: 2 / 4 / 1.5 / 4;
        max-width: 100px;
        text-align: right;
      }
    }

    @media screen and (max-width: 490px) {
      &:nth-child(5) {
        max-width: 120px;
      }
    }

    @media screen and (max-width: 470px) {
      &:nth-child(5) {
        max-width: 80px;
      }
    }

    &:nth-child(3) {
      padding-left: 32px;

      @media screen and (max-width: $breakpointDownLg) {
        text-align: left;
        padding-left: 20px;
      }

      @media screen and (max-width: $breakpointDownXs) {
        text-align: left;
        padding-left: 20px;
      }
    }
  }
}

.purchase-row {
  margin: 0 0 35px;
  padding: 0 30px;

  @media screen and (max-width: $breakpointDownSm) {
    padding: 0 8px 0 10px;
  }

  &:last-child {
    margin: 0;
  }
}

.pagination-bottom {
  display: flex;
  justify-content: center;
  margin: 35px 0 0;
}

.checkbox-wrapper {
  display: flex;
  padding: 0 0 30px;
  max-width: 1200px;
  margin: 0 auto;

  .base-checkbox {
    &:first-child {
      margin: 0 10px 0 0;
    }
  }
}
</style>
